import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  User,
  Clock, 
  FileText, 
  DollarSign, 
  Calendar, 
  Settings,
  Home,
  Shield,
  AlertTriangle,
  Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/components/auth/AuthProvider';

interface SidebarItem {
  title: string;
  icon: React.ComponentType<any>;
  path: string;
  badge?: string;
  requiredRole?: string[];
}

const sidebarItems: SidebarItem[] = [
  {
    title: 'Dashboard',
    icon: Home,
    path: '/admin-dashboard'
  },
  {
    title: 'Employee Portal',
    icon: User,
    path: '/employee-portal'
  },
  {
    title: 'My Profile',
    icon: User,
    path: '/my-profile'
  },
  {
    title: 'Employee Management',
    icon: Users,
    path: '/employee-management',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'Time & Attendance',
    icon: Clock,
    path: '/hr/time-calendar'
  },
  {
    title: 'Scheduling',
    icon: Calendar,
    path: '/hr/scheduling',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'Payroll',
    icon: DollarSign,
    path: '/payroll-processing',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'Documents',
    icon: FileText,
    path: '/hr/documents',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'Certifications',
    icon: Award,
    path: '/hr/certifications',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'HR Alerts',
    icon: AlertTriangle,
    path: '/hr/alerts',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'User Management',
    icon: Users,
    path: '/hr/user-management',
    requiredRole: ['admin', 'manager']
  },
  {
    title: 'Admin Tools',
    icon: Shield,
    path: '/hr/admin',
    requiredRole: ['admin']
  }
];

export const EmployeeSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAdmin, isManager } = useAuth();

  const hasAccess = (item: SidebarItem): boolean => {
    if (!item.requiredRole) return true;
    
    const userRole = profile?.role || 'employee';
    if (isAdmin && item.requiredRole.includes('admin')) return true;
    if (isManager && item.requiredRole.includes('manager')) return true;
    if (item.requiredRole.includes(userRole)) return true;
    
    return false;
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
      <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r bg-card px-6 pb-4">
        <div className="flex h-16 shrink-0 items-center">
          <h2 className="text-lg font-semibold text-foreground">
            HR Management
          </h2>
        </div>
        
        <nav className="flex flex-1 flex-col">
          <ScrollArea className="flex-1">
            <ul role="list" className="flex flex-1 flex-col gap-y-2">
              {sidebarItems.map((item) => {
                if (!hasAccess(item)) return null;
                
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <li key={item.path}>
                    <Button
                      variant={active ? "secondary" : "ghost"}
                      className={`w-full justify-start h-auto p-3 ${
                        active 
                          ? 'bg-secondary text-secondary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                      onClick={() => navigate(item.path)}
                    >
                      <Icon className="h-5 w-5 mr-3 shrink-0" />
                      <span className="truncate">{item.title}</span>
                      {item.badge && (
                        <span className="ml-auto rounded-full bg-primary px-2 py-1 text-xs text-primary-foreground">
                          {item.badge}
                        </span>
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
          
          <Separator className="my-4" />
          
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-auto p-3 text-muted-foreground hover:text-foreground hover:bg-accent"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-5 w-5 mr-3 shrink-0" />
              Settings
            </Button>
          </div>
        </nav>
      </div>
    </div>
  );
};